/**
 * Хранение решения о необязательных технологиях — § 25 TDDDG.
 *
 * Четыре правила, каждое из которых — норма, а не вкус:
 *
 * 1. **до согласия ничего не грузится.** Согласие спрашивается *до* доступа к
 *    устройству (§ 25 Abs. 1 TDDDG); визуально корректный баннер, который не
 *    блокирует скрипты, не считается. Отсюда `isAllowed` — единственная точка,
 *    через которую подключается любая необязательная технология;
 * 2. **отсутствие решения ≠ согласие.** Пустое хранилище означает «нет», а не
 *    «пока не спросили»;
 * 3. **отказ так же прост, как согласие** (OH DSK «Digitale Dienste» v1.2,
 *    EDSA 03/2022): «Alle akzeptieren» и «Nur notwendige» — один клик каждая,
 *    на одном уровне;
 * 4. **решение отзывается так же просто, как даётся.** Отсюда постоянная
 *    ссылка «Cookie-Einstellungen» в подвале и в правовой полосе приложения.
 *
 * `localStorage`, а не cookie: само согласие — не «технически необходимая
 * cookie», и отправлять его на сервер с каждым запросом незачем.
 */

/** Категории. `necessary` в выбор не входит: она не отключается. */
export const CONSENT_CATEGORIES = ['functional', 'analytics', 'marketing'] as const;

export type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];

export type ConsentDecision = Record<ConsentCategory, boolean>;

export interface StoredConsent {
  decision: ConsentDecision;
  /** ISO 8601. Нужна, чтобы показать, когда решение было принято. */
  decidedAt: string;
  /**
   * Категории, которые действовали на момент решения. Появление новой
   * категории обязано снова спросить: согласие на «аналитику» не покрывает
   * «маркетинг», который добавили позже.
   */
  scope: ConsentCategory[];
  /** Версия формата записи: смена формата не должна молча читаться как согласие. */
  version: 1;
}

export const CONSENT_STORAGE_KEY = 'carviq.consent';

export const DENY_ALL: ConsentDecision = {
  functional: false,
  analytics: false,
  marketing: false,
};

export const ALLOW_ALL: ConsentDecision = {
  functional: true,
  analytics: true,
  marketing: true,
};

function isCategory(value: string): value is ConsentCategory {
  return (CONSENT_CATEGORIES as readonly string[]).includes(value);
}

/** Отфильтровать объявленные оператором категории, отбросив неизвестные. */
export function knownCategories(declared: readonly string[]): ConsentCategory[] {
  return declared.filter(isCategory);
}

export function readConsent(): StoredConsent | null {
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const candidate = parsed as {
      version?: unknown;
      decision?: unknown;
      decidedAt?: unknown;
      scope?: unknown;
    };
    if (
      candidate.version !== 1 ||
      typeof candidate.decision !== 'object' ||
      candidate.decision === null
    ) {
      return null;
    }

    const stored = candidate.decision as Record<string, unknown>;
    const decision = { ...DENY_ALL };
    for (const category of CONSENT_CATEGORIES) {
      // Согласием считается только явное `true`. Любое другое значение —
      // «нет»: повреждённая запись не может открывать трекеры.
      decision[category] = stored[category] === true;
    }
    return {
      decision,
      decidedAt: typeof candidate.decidedAt === 'string' ? candidate.decidedAt : '',
      scope: Array.isArray(candidate.scope) ? knownCategories(candidate.scope as string[]) : [],
      version: 1,
    };
  } catch {
    // Приватный режим, отключённое хранилище, повреждённая запись — во всех
    // случаях «согласия нет». Ошибка чтения не может означать «да».
    return null;
  }
}

export function writeConsent(decision: ConsentDecision, scope: ConsentCategory[]): StoredConsent {
  const record: StoredConsent = {
    decision,
    decidedAt: new Date().toISOString(),
    scope,
    version: 1,
  };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Запись не удалась — решение действует на текущую сессию и будет
    // спрошено снова. Молча считать это согласием нельзя.
  }
  return record;
}

/**
 * Нужно ли спрашивать: есть объявленные категории и по какой-то из них решения
 * ещё нет.
 *
 * Пустой `declared` означает, что необязательных технологий нет вовсе —
 * баннер не показывается. Баннер «ни о чём» обесценивает согласие там, где оно
 * однажды понадобится.
 */
export function needsDecision(stored: StoredConsent | null, declared: readonly string[]): boolean {
  const categories = knownCategories(declared);
  if (categories.length === 0) return false;
  if (stored === null) return true;
  return categories.some((category) => !stored.scope.includes(category));
}
